import type {
  SankeyLink as D3SankeyLink,
  SankeyNode as D3SankeyNode,
} from 'd3-sankey';

/** Node in a multi-level Sankey graph; its column is derived from topology. */
export type SankeyNode = {
  id: string;
  /** Falls back to `id` when omitted. */
  label?: string;
  color?: string;
  layer?: number;
};

/** Link connecting a source node to a target node by id. */
export type SankeyLink = {
  source: string;
  target: string;
  value: number;
  color?: string;
  /** Pre-computed tooltip share, carried from the data source. */
  share?: number;
  /** Links sharing a `group` highlight together on hover. */
  group?: string;
  /** Raw value for tooltips; set when the layout floors a hairline flow. */
  rawValue?: number;
};

export type SankeyData = {
  nodes: SankeyNode[];
  links: SankeyLink[];
};

export type SankeyScale = {
  totalExponent: number;
  shareExponent: number;
};

/** A laid-out node with geometry fields narrowed to non-optional. */
export type SankeyNodeValidated = D3SankeyNode<SankeyNode, SankeyLink> & {
  x0: number;
  x1: number;
  y0: number;
  y1: number;
  depth: number;
  value: number;
};

/** A laid-out link with geometry and resolved node refs narrowed. */
export type SankeyLinkValidated = D3SankeyLink<SankeyNode, SankeyLink> & {
  width: number;
  y0: number;
  y1: number;
  source: SankeyNodeValidated;
  target: SankeyNodeValidated;
  rawValue?: number;
};

/** A link prepared for rendering: SVG path plus display-only fields. */
export type SankeyRenderLink = {
  key: string;
  index: number;
  path: string;
  color?: string;
  width: number;
  sourceX: number;
  sourceY: number;
  targetX: number;
  targetY: number;
  source: SankeyNodeValidated;
  target: SankeyNodeValidated;
  rawValue: number;
  displayShare?: number;
  hubIsTarget: boolean;
  group?: string;
};

export const getSankeyNodeLabel = (node: SankeyNode): string =>
  (node.label ?? node.id).replace(/,\s*Inc\.?$/i, '');
